import * as _ from "lodash";
import Config from "@skin_config";
import { Swarm } from "@lbs/swarm-client";
import Helpers from "@common/helpers/helperFunctions";
import Translator from "@common/helpers/translator";
import { persistStore } from "redux-persist";
import { setOddConverterPartnerConfig } from "@common/helpers/oddConverter";
import { swarmRequest } from "@common/helpers/util/requestMiddleware";
import { getAuthData } from "@common/helpers/auth";
import { getPreferencesContextValue } from "@common/contexts/preferences";
import { broadcast, broadcastChannels } from "@common/helpers/broadcast";
import { checkTerminalMode } from "@common/helpers";
import { setFontCSSVariables } from "@common/helpers/view";
import {
  SetLanguage,
  SetTerminalAuthData,
  SetBonuses,
  SetTax,
  getMerchantInfo,
  getSavedPlayerPaymentInfo,
  setLastMoneyInputType,
  setTerminalDepositMode,
} from "../actions/terminal";
import { AppReady } from "../actions";
import countries from "../constants/countries";
import { updateTranslations } from "../constants/errorCodes";
import { SwarmConfigData } from "../actions/initialData";
import setupTerminalFunctionalities from "../helpers/terminalSpecific";
import { setSwarmBetshopSessionInfo } from "../helpers/terminalRequests/cashdesk";
import PaymentsService from "../services/payments";
import { isMultiDepositModeEnabled } from "../helpers/multiDepositMode";
import { getNetworkTime } from "../../../BettingTerminal.Common/helpers/networkTime";
import { TerminalBalanceFetcher } from "../helpers/terminalBalance";
import { getTerminalLanguage } from "../helpers/languageByIdOrConfig";
import { TerminalDepositModesEnum } from "../constants/enums/terminalDepositModes";
import { restoreLogin } from "../helpers/login";

Config.main.enableTouchToClick &&
  window.addEventListener("touchstart", (e) => {
    if (e.touches.length > 1) e.target.click();
  });

const setupTerminalParams = async (store) => {
  const pid = Helpers.getUriParam("pid");
  const tid = Helpers.getUriParam("tid");
  let token = Helpers.getUriParam("token");
  const currency = Helpers.getUriParam("currency");
  const version = Helpers.getUriParam("version");
  const dualscreen = Helpers.getUriParam("dualscreen");
  const skinType = Helpers.getUriParam("skin");
  const betshopid = Helpers.getUriParam("betshopid");
  const enableOnlinePayment = Helpers.getUriParam("enableOnlinePayment");
  const swarmLink = Helpers.getUriParam("swarm");
  token = token && token.replace(" ", "+"); // todo delete after Khikar makes release

  if (token || pid || tid || currency || skinType || betshopid || enableOnlinePayment || swarmLink) {
    sessionStorage.setItem(
      "authData",
      JSON.stringify({
        pid,
        tid,
        token,
        currency,
        skinType,
        betshopid,
        enableOnlinePayment,
        swarmLink,
      })
    );
  } else {
    const authData = JSON.parse(sessionStorage.getItem("authData"));
    if (!authData || !Object.keys(authData).length) {
      sessionStorage.setItem("authData", JSON.stringify(Config.defaultTerminalParams));
    }
  }
  dualscreen !== undefined && sessionStorage.setItem("dualscreen", dualscreen);
  version !== undefined && sessionStorage.setItem("version", JSON.stringify({ version }));

  if (!_.isEmpty(Config.main.blockedProviders)) {
    const country = await fetch("https://ipapi.co/json").then((res) => res.json());
    localStorage.setItem("countryCode", country?.country_code);
  }

  const authData = JSON.parse(sessionStorage.getItem("authData"));
  store.dispatch(SetTerminalAuthData({ authData }));
};

const sendLanguageToApp = (store) => {
  const [preferences] = getPreferencesContextValue();
  const { lang } = preferences;
  setTimeout(() => store.dispatch(SetLanguage(Config.swarm.languageMap[lang] || lang)), 0);
};
const initTerminalDepositMode = async (store, balance) => {
  const { state: sessionBalanceState, playerInfo } = await PaymentsService.getSessionBalanceState();

  if (sessionBalanceState === TerminalDepositModesEnum.Cash && balance === 0) {
    await PaymentsService.clearSessionBalanceState()
    store.dispatch(setLastMoneyInputType(TerminalDepositModesEnum.Ticket));
    store.dispatch(setTerminalDepositMode(TerminalDepositModesEnum.Ticket))

    return
  };

  if (sessionBalanceState) {
    store.dispatch(setLastMoneyInputType(sessionBalanceState));
    store.dispatch(setTerminalDepositMode(sessionBalanceState));

  }

  if (playerInfo?.PersonalId) await PaymentsService.setPlayerInfo(playerInfo);
};
const Bootstrap = async (store) => {
  setFontCSSVariables(Config.main.fontName);

  await loadConfig(store);
  await setupTerminalParams(store);

  return loadTranslations(store).then(() => {
    updateTranslations();
    setupTerminalFunctionalities(store).then(async () => {
      sendLanguageToApp(store);
      store.dispatch(getMerchantInfo());
      store.dispatch(getSavedPlayerPaymentInfo());

      if (checkTerminalMode(Config, "betting")) {
        // Await initSwarm before restoreLogin: the swarm session must be
        // open before `restore_login` can fire. Previously this was
        // fire-and-forget; the await is safe because every subsequent
        // step (balance fetch, bonus rules) needs swarm anyway.
        await initSwarm(store);
        // Silently re-establish the previous player session if
        // `{auth_token, user_id}` is in localStorage. On success the
        // response flows through the same `makeLogin` pipeline as a
        // regular login, so the rest of the app sees a normal logged-in
        // state with no extra wiring.
        await restoreLogin();
        await TerminalBalanceFetcher.fetchAndStore();

        await new Promise((resolve) => {
          setTimeout(async () => {
            const balance = store.getState().terminal.balance?.session_balance

            if (isMultiDepositModeEnabled()) await initTerminalDepositMode(store, balance);
            resolve()
          }, 0)
        })

        if (Config.betslip.enableExpressBonus) {
          await swarmRequest({ include_exact_losing: true }, "get_sport_bonus_rules").then((response) =>
            store.dispatch(SetBonuses(response?.details))
          );
        }
      }

      store.dispatch(AppReady());
      window.appReady = store.getState().appReady;
      window.jsobject ? window.jsobject.appReady() : window.external.appReady();
    });
  });
};

export default Bootstrap;

const initSwarm = async (store) => {
  const [{ url: swarmUrl }] = Config.swarm.websocket;
  const [{ lang }] = getPreferencesContextValue();
  const terminalLang = getTerminalLanguage();
  const sessionLanguage = lang || terminalLang;
  const langToUse = Config.swarm.languageMap[sessionLanguage] || sessionLanguage;

  const swarmSessionParams = {
    language: langToUse,
    site_id: getAuthData()?.pid,
    terminal: getAuthData()?.tid || Config.defaultTerminalParams.tid,
  };

  const authDataLink = getAuthData()?.swarmLink;

  const swarmLink = authDataLink ? `wss://${authDataLink}.betconstruct.com` : swarmUrl;

  const swarmInstance = Swarm.init(swarmLink, swarmSessionParams, Config.swarm);

  swarmInstance.listeners.addEventListener("connected", (response) => {
    const data = { connected: true };
    broadcast.dispatchEvent(new CustomEvent(broadcastChannels.SocketStateChange, { detail: data }));

    const authData = getAuthData();

    if (authData) setSwarmBetshopSessionInfo();
  });

  swarmInstance.listeners.addEventListener("closed", () => {
    const data = { connected: false };
    broadcast.dispatchEvent(new CustomEvent(broadcastChannels.SocketStateChange, { detail: data }));
  });

  await swarmInstance.requestSession();

  const partnerConfig = swarmInstance.getPartnerConfig();

  setPartnerConfig(store, partnerConfig);
  swarmInstance.syncNetworkTime(getNetworkTime);
};

window.killSwarm = () => {
  Swarm.getInstance()._socket?.close();
};

const TRANSLATIONS_BASE_URL = 'https://lbs-translations.betconstruct.com/terminalPro';

/**

Load translations
@returns {Promise}
*/
function loadTranslations(store) {
  return new Promise(async (resolve) => {
    const [preferences, setPreferences] = getPreferencesContextValue();
    const languageCodes = Object.keys(Config.main.availableLanguages);
    const countryCode = countries.iso2ToIso3[preferences.geoData && preferences.geoData.countryCode];
    const isLanguageAvailable = Config.main.availableLanguages.hasOwnProperty(countryCode);
    const terminalLang = getTerminalLanguage();
    const noTransKey = Helpers.getQueryStringValue("?notrans") === "id";
    const notrans = Helpers.getQueryStringValue("notrans") !== null;
    const lang = noTransKey
      ? "notrans"
      : preferences.lang ||
      (preferences.geoData && isLanguageAvailable && countryCode.toLowerCase()) ||
      terminalLang;
    Translator.setLanguage(lang);
    setPreferences({ ...preferences, lang });
    if (notrans && !noTransKey) resolve();
    else {
      try {
        await Promise.all(
          languageCodes.map((lang) =>
            fetch(`${TRANSLATIONS_BASE_URL}/${lang}.json?v=${Date.now()}`)
              .then((response) => response.json())
              .then((json) => {
                Translator.setTranslations(json, lang);
              })
              .catch((err) => {
                console.warn(`Translation not loaded for "${lang}"`, err);
              })
          )
        );

        resolve();
      } catch (ex) {
        console.warn("translations not loaded", ex);
        resolve(ex);
      }
    }
  });
}
const setPartnerConfig = (store, partnerConfig) => {
  if (!partnerConfig) return;

  setOddConverterPartnerConfig(partnerConfig.price_round_method);

  // Internal bet builder is now partner-controlled — `bet_builder_ih_enabled`
  // on the partner_config is the single source of truth. The skin config no
  // longer carries `enableInternalBetBuilder`; we inject the partner value
  // into `Config.sportsbook` here so anything consuming `Config.sportsbook`
  // (sportsbookWrapper -> Sportsbook MFE, betBuilderIcon, etc.) picks it up.
  // This runs before the SportsbookWrapper mounts (AppReady is dispatched
  // after this in Bootstrap), so the value is available by first render.
  if (Config.sportsbook) {
    Config.sportsbook.enableInternalBetBuilder = !!partnerConfig.bet_builder_ih_enabled;
  }

  store.dispatch(SwarmConfigData(partnerConfig, "partner"));
  store.dispatch(
    SetTax({
      taxType: partnerConfig.tax_type,
      taxPercent: partnerConfig.tax_percent,
    })
  );
};
/**

Loads stored staloadConfige from local config and puts into store
Loads config froloadConfig external file
Merges external config with Config object
Merges saved preferences from store with ones defined in config
@param store
@returns {Promise}
*/
function loadConfig(store) {
  const notrans = Helpers.getQueryStringValue("?notrans") === "id";
  let allDone;
  const persistentStoreKeys = ["persistentUIState", "favorites"];
  const options = {
    whitelist: persistentStoreKeys,
  };
  const storeLoaded = new Promise((resolve) => persistStore(store, (() => options)(), resolve));
  let configLoaded; // load external config and merge with Config
  configLoaded = new Promise((resolve) => {
    if (Config.main.disableRemoteConfig) {
      resolve();
    } else {
      const configUrl = location.hostname === "localhost" ? "/conf.local.json" : "/conf.json";
      fetch(configUrl)
        .then((response) => response.json())
        .then((json) => {
          Helpers.mergeRecursive(Config, json);
          console.log("Merged config:", Config);
          resolve();
        })
        .catch((ex) => {
          console.warn("external config not loaded", ex, ", proceeding with default one");
          resolve(ex);
        });
    }
  });
  Promise.all([storeLoaded, configLoaded]).then(() => {
    const [preferences] = getPreferencesContextValue(); // Merge saved preferences from state with Config.env
    const terminalLang = getTerminalLanguage();
    let defaultLanguage = preferences.lang ? preferences.lang : terminalLang;
    const init = {
      ...preferences,
      lang: defaultLanguage,
    };
    defaultLanguage = Config.swarm.languageMap[defaultLanguage] || defaultLanguage;
    // store.dispatch(SetLanguage(defaultLanguage));
    // Config.betting.enableExpressBonus &&
    //  checkTerminalMode(Config, "betting") &&
    //  swarmRequest({}, "get_sport_bonus_rules").then(response =>
    //    store.dispatch(SetBonuses(response?.details))
    //  );
    if (preferences) {
      Helpers.mergeRecursive(Config.env, init);
    }
    allDone(notrans);
  });
  return new Promise((resolve) => {
    allDone = resolve;
  });
}
